<template>
  <div 
    class="glass-table-wrapper" 
    :class="{ 'glass-table-wrapper--loading': loading }"
  >
    <!-- Loading overlay -->
    <div v-if="loading" class="glass-table__loading">
      <div class="spinner"></div>
    </div>
    
    <!-- Table -->
    <table class="glass-table">
      <thead>
        <tr>
          <th 
            v-for="col in columns" 
            :key="col.key"
            :style="{ width: col.width }"
            :class="{ 'sortable': col.sortable }"
            @click="col.sortable && handleSort(col.key)"
          >
            <div class="th-content">
              <span>{{ col.label }}</span>
              <span v-if="col.sortable" class="sort-icon">
                <i v-if="sortKey === col.key" :class="sortDesc ? 'fas fa-sort-down' : 'fas fa-sort-up'"></i>
                <i v-else class="fas fa-sort"></i>
              </span>
            </div>
          </th>
          <th v-if="$scopedSlots.actions" class="actions-col">Actions</th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="sortedItems.length === 0">
          <td :colspan="columns.length + ($scopedSlots.actions ? 1 : 0)" class="empty-message">
            <slot name="empty">
              <div class="empty-state">
                <i class="fas fa-inbox"></i>
                <p>{{ emptyText }}</p>
              </div>
            </slot>
          </td>
        </tr>
        <tr 
          v-for="(item, index) in sortedItems" 
          :key="item[itemKey] || index"
          :class="{ 'clickable': rowClickable }"
          @click="rowClickable && $emit('row-click', item)"
        >
          <td v-for="col in columns" :key="col.key">
            <slot :name="`cell(${col.key})`" :item="item" :value="getValue(item, col.key)">
              {{ formatValue(getValue(item, col.key), col.format) }}
            </slot>
          </td>
          <td v-if="$scopedSlots.actions" class="actions-col">
            <slot name="actions" :item="item"></slot>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
export default {
  name: 'GlassTable',
  props: {
    items: {
      type: Array,
      default: () => []
    },
    columns: {
      type: Array,
      required: true
      // Format: [{ key: 'name', label: 'Nom', sortable: true, width: '200px', format: 'date' }]
    },
    itemKey: {
      type: String,
      default: 'id'
    },
    loading: {
      type: Boolean,
      default: false
    },
    rowClickable: {
      type: Boolean,
      default: false
    },
    emptyText: {
      type: String,
      default: 'Aucune donnée disponible'
    }
  },
  data() {
    return {
      sortKey: null,
      sortDesc: false
    };
  },
  computed: {
    sortedItems() {
      if (!this.sortKey) return this.items;
      
      return [...this.items].sort((a, b) => {
        const aVal = this.getValue(a, this.sortKey);
        const bVal = this.getValue(b, this.sortKey);
        
        let result = 0;
        if (aVal < bVal) result = -1;
        if (aVal > bVal) result = 1;
        
        return this.sortDesc ? -result : result;
      });
    }
  },
  methods: {
    getValue(item, key) {
      // Support nested keys like "owner.name"
      return key.split('.').reduce((obj, k) => obj?.[k], item);
    },
    formatValue(value, format) {
      if (value === null || value === undefined) return '-';
      
      switch (format) {
        case 'date':
          return new Date(value).toLocaleDateString('fr-FR');
        case 'datetime':
          return new Date(value).toLocaleString('fr-FR');
        case 'currency':
          return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(value);
        case 'boolean':
          return value ? 'Oui' : 'Non';
        default:
          return value;
      }
    },
    handleSort(key) {
      if (this.sortKey === key) {
        this.sortDesc = !this.sortDesc;
      } else {
        this.sortKey = key;
        this.sortDesc = false;
      }
      this.$emit('sort', { key: this.sortKey, desc: this.sortDesc });
    }
  }
};
</script>

<style scoped>
.glass-table-wrapper {
  position: relative;
  background: rgba(255, 255, 255, 0.3);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 16px;
  overflow: hidden;
}

.glass-table-wrapper--loading {
  pointer-events: none;
}

.glass-table__loading {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
}

.spinner {
  width: 32px;
  height: 32px;
  border: 3px solid rgba(var(--theme-primary-rgb), 0.2);
  border-top-color: var(--theme-primary);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.glass-table {
  width: 100%;
  border-collapse: collapse;
}

.glass-table thead {
  background: rgba(255, 255, 255, 0.4);
}

.glass-table th {
  padding: 1rem 1.25rem;
  text-align: left;
  font-weight: 600;
  font-size: 0.8125rem;
  color: #424242;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.glass-table th.sortable {
  cursor: pointer;
  user-select: none;
}

.glass-table th.sortable:hover {
  background: rgba(59, 136, 165, 0.1);
}

.th-content {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.sort-icon {
  color: rgba(66, 66, 66, 0.4);
  font-size: 0.75rem;
}

.glass-table td {
  padding: 1rem 1.25rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  color: #424242;
}

.glass-table tbody tr {
  transition: background 0.2s ease;
}

.glass-table tbody tr:hover {
  background: rgba(255, 255, 255, 0.5);
}

.glass-table tbody tr.clickable {
  cursor: pointer;
}

.glass-table tbody tr:last-child td {
  border-bottom: none;
}

.actions-col {
  width: 120px;
  text-align: right;
}

.empty-message {
  text-align: center;
  padding: 3rem !important;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  color: rgba(66, 66, 66, 0.5);
}

.empty-state i {
  font-size: 2.5rem;
}

.empty-state p {
  margin: 0;
  font-size: 0.9375rem;
}

/* Responsive */
@media (max-width: 768px) {
  .glass-table-wrapper {
    overflow-x: auto;
  }
  
  .glass-table {
    min-width: 600px;
  }
  
  .glass-table th,
  .glass-table td {
    padding: 0.75rem 1rem;
  }
}
</style>
